import { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import * as jose from "jose";
import Homepage from "./component/Homepage";
import AuthLogin from "./component/Auth/login";
import AuthRegistration from "./component/Auth/registration";
import AuthResetPassword from "./component/Auth/reset-password";
import AuthSetPassword from "./component/Auth/set-password";
import { ROLE, userContext } from "./context/user";
import { appToastContext } from "./context/toast";
import InstitutionList from "./component/Institutions/list";
import InstitutionForm from "./component/Institutions/form";
import RepresentativeList from "./component/Representatives/list";
import RepresentativeForm from "./component/Representatives/form";
import Layout from "./component/Layout";
import { useLogin } from "./api/queries/auth/useLogin";
import {
  AuthLoginPayload,
  AuthRefreshPayload,
  AuthRefreshResponseData,
} from "./api/types/auth";
import { Toast } from "primereact/toast";
import { useQueryClient } from "react-query";
import { ErrorResponseData } from "./api/types/base";
import InstitutionRankingList from "./component/InstitutionRankings/list";
import InstitutionRankingForm from "./component/InstitutionRankings/form";
import ChecklistList from "./component/Checklists/list";
import ChecklistForm from "./component/Checklists/form";
import api from "./api/client";
import { storage } from "./common/storage";
import { useLogout } from "./api/queries/auth/useLogout";
import InteractionList from "./component/Interactions/list";
import InteractionForm from "./component/Interactions/form";
import { useUserInfo } from "./api/queries/user/useUserInfo";
import UserList from "./component/Users/list";
import UserDetailComponent from "./component/Users/detail";
import UserProfile from "./component/Users/profile";
import PlacementEnvironmentList from "./component/PlacementEnviroment/list";
import PlacementEnvironmentDetail from "./component/PlacementEnviroment/detail";
import PlacementEnvironmentEdit from "./component/PlacementEnviroment/edit";
import PlacementEnvironmentInstitutionForm from "./component/PlacementEnviroment/institution-form";
import PlacementEnvironmentInstitutionRankingForm from "./component/PlacementEnviroment/institution-ranking";
import PlacementEnvironmentTemplateList from "./component/PlacementEnvironmentTemplates/list";
import PlacementEnvironmentTemplateForm from "./component/PlacementEnvironmentTemplates/form";
import UserPlacementEnvironmentComponent from "./component/Users/placement-environment";
import PlacementEnvironmentForm from "./component/PlacementEnviroment/form";
import InstitutionApproveForm from "./component/Institutions/approve-form";
import { Admin } from "./component/admin";
import ContextForm from "./component/admin/context/form";
import AuthVerifyEmail from "./component/Auth/verify-email";
import ContextList from "./component/admin/context/list";
import PeopleList from "./component/people/list";
import PeopleForm from "./component/people/form";
import PeopleShow from "./component/people/show";
import AccountList from "./component/account/list";
import AccountForm from "./component/account/form";
import FieldList from "./component/admin/field/list";
import CampusActivityList from "./component/admin/campus-activity/list";
import FieldForm from "./component/admin/field/form";
import CampusActivityForm from "./component/admin/campus-activity/form";
import TagList from "./component/Tag/list";
import TagForm from "./component/Tag/form";
import MetabaseExamplePage from "./component/metabase/MetabaseExamplePage";
import FieldTypesList from "./component/Crm/field-types/list";
import FieldTypeForm from "./component/Crm/field-types/form";
import FieldTypeEdit from "./component/Crm/field-types/edit";
import TallyFormQuestions from "./component/Tally/FormQuestions";
import TallyFormDetail from "./component/Tally/FormDetail";
import TallyFormList from "./component/Tally/ListForms";
import ConnectionConfirmation from "./component/Connection/Confirmation";
import CheckInResponse from "./component/Connection/CheckInResponse";
import PeopleConnectionList from "./component/admin/people-connection/list";
import StoryInteractionsList from "./component/admin/story-interactions/list";
import VoiceRequestsList from "./component/admin/voice-requests/list";
import AccessDenied from "./component/AccessDenied";

export default function App() {
  const TEN_MINUTES_MILISECONDS = 10 * 60 * 1000;

  const authLogin = useLogin();
  const authLogout = useLogout();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const appToastRef = useRef<Toast>(null);
  const currentToken = storage.get("token");
  const {
    data: userInfo,
    refetch: userInfoRefetch,
    status: userInfoStatus,
  } = useUserInfo();

  const [inited, setInited] = useState<boolean>(false);

  if (currentToken) {
    api.defaults.headers.common.Authorization = `Bearer ${currentToken}`;
  }

  const login = async (payload: AuthLoginPayload) => {
    try {
      const { token, refresh_token } = await authLogin.mutateAsync(payload);
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      storage.set("token", token);
      storage.set("refresh_token", refresh_token);
      userInfoRefetch();
      navigate("/");
    } catch {
      // Error is handled by the global onError handler (shows toast)
    }
  };

  const logout = async () => {
    await authLogout.mutateAsync();
    api.defaults.headers.common.Authorization = "";
    storage.delete("token");
    storage.delete("refresh_token");
    queryClient.resetQueries();
    navigate("/login");
  };

  const hasRole = (name: string): boolean =>
    userInfo ? userInfo.data.roles.indexOf(name) > -1 : false;

  const isAdmin = (): boolean => hasRole(ROLE.ADMIN);
  const isAccountManager = (): boolean => hasRole(ROLE.ACCOUNT_MANAGER);

  const isPeopleEditor = (): boolean => hasRole(ROLE.PEOPLE_EDITOR);

  const isProfileManager = (): boolean => hasRole(ROLE.PEOPLE_MANAGER);

  const isProfileManagerTemp = (): boolean =>
    hasRole(ROLE.ROLE_PROFILE_MANAGER_TEMP_APPROVED);

  api.interceptors.request.use(
    async (config) => {
      const { url } = config;
      const storageToken = storage.get("token");
      const refreshToken = storage.get("refresh_token");

      if (storageToken) {
        const { exp } = jose.decodeJwt(storageToken);

        if (exp) {
          const expires = exp * 1000 - TEN_MINUTES_MILISECONDS;

          if (url !== "/auth/refresh" && refreshToken && expires < Date.now()) {
            const payload: AuthRefreshPayload = {
              refresh_token: refreshToken,
            };

            const { data }: { data: AuthRefreshResponseData } = await api.post(
              "/auth/refresh",
              payload,
            );

            const { token, refresh_token } = data;

            api.defaults.headers.common.Authorization = `Bearer ${token}`;
            storage.set("token", token);
            storage.set("refresh_token", refresh_token);
          }
        }
      }

      return config;
    },
    (err) => {
      return Promise.reject(err);
    },
  );

  const isPublicUrl: boolean =
    /\/login|\/registration|\/reset-password|\/set-password|\/email-verification|\/connection/.test(
      pathname,
    );

  useEffect(() => {
    if (!currentToken && !isPublicUrl) {
      navigate("/login");
    }
  }, [currentToken, navigate, isPublicUrl]);

  useEffect(() => {
    const queryCache = queryClient.getQueryCache();
    const mutationCache = queryClient.getMutationCache();

    const onError = ({ response }: any) => {
      if (!response) {
        appToastRef.current?.show({
          severity: "error",
          summary: "Request could not be completed.",
        });
        return;
      }

      const { status } = response;
      const { data, success, message } = response.data as ErrorResponseData;

      if (status === 401) {
        appToastRef.current?.show({ severity: "error", summary: message });
        storage.delete("token");

        if (!isPublicUrl) {
          navigate("/login");
        }
      } else if (
        status === 403 &&
        message.toLocaleLowerCase() === "access denied"
      ) {
        // TODO: do not show "Access Denied" message
      } else {
        if (!success) {
          if (message) {
            appToastRef.current?.show({
              severity: "info",
              summary: message,
            });
          }

          if (data && data.errors) {
            appToastRef.current?.show(
              data.errors.map((err) => ({
                severity: "error",
                summary: `${err.property} - ${err.message}`,
              })),
            );
          }
        }
      }
    };

    queryCache.config.onError = onError;
    mutationCache.config.onError = onError;
  }, [queryClient, navigate, isPublicUrl]);

  useEffect(() => {
    if (userInfoStatus !== "loading") {
      setInited(true);
    }
  }, [userInfoStatus]);

  if (!inited) {
    return <></>;
  }

  return (
    <userContext.Provider
      value={{
        detail: userInfo?.data,
        login,
        logout,
        hasRole,
        isAdmin,
        isAccountManager,
        isPeopleEditor,
        isProfileManager,
        isProfileManagerTemp,
      }}
    >
      <appToastContext.Provider value={{ ref: appToastRef }}>
        <Toast ref={appToastRef} />

        <Routes>
          <Route path={"/login"} element={<AuthLogin />} />
          <Route path={"/registration"} element={<AuthRegistration />} />
          <Route path={"/reset-password"} element={<AuthResetPassword />} />
          <Route path={"/set-password/:token"} element={<AuthSetPassword />} />
          <Route path={"/email-verification"} element={<AuthVerifyEmail />} />
          <Route
            path={"/connection/:id/response"}
            element={<ConnectionConfirmation />}
          />
          <Route
            path={"/connection/:id/check-in"}
            element={<CheckInResponse />}
          />

          <Route path={"/"} element={<Layout />}>
            <Route
              path={"journey-tracker-templates"}
              element={<PlacementEnvironmentTemplateList />}
            />
            <Route
              path={"journey-tracker-templates/new"}
              element={<PlacementEnvironmentTemplateForm />}
            />
            <Route
              path={"journey-tracker-templates/:uuid"}
              element={<PlacementEnvironmentTemplateForm />}
            />

            <Route
              path={"journey-trackers"}
              element={<PlacementEnvironmentList />}
            />
            <Route
              path={"journey-trackers/new"}
              element={<PlacementEnvironmentForm />}
            />
            <Route
              path={"journey-trackers/:uuid"}
              element={<PlacementEnvironmentDetail />}
            />
            <Route
              path={"journey-trackers/:uuid/institution"}
              element={<PlacementEnvironmentInstitutionForm />}
            />
            <Route
              path={"journey-trackers/:uuid/:institutionUuid"}
              element={<PlacementEnvironmentEdit />}
            />
            <Route
              path={
                "journey-trackers/:uuid/institution-ranking/:institutionUuid/:rankingUuid"
              }
              element={<PlacementEnvironmentInstitutionRankingForm />}
            />
            <Route
              path={
                "journey-trackers/:uuid/institution-ranking/:institutionUuid/new"
              }
              element={<PlacementEnvironmentInstitutionRankingForm />}
            />

            <Route path={"institutions"} element={<InstitutionList />} />
            <Route path={"institutions/new"} element={<InstitutionForm />} />
            <Route
              path={"institutions/approve"}
              element={<InstitutionApproveForm />}
            />
            <Route path={"institutions/:uuid"} element={<InstitutionForm />} />

            <Route
              path={"institution-rankings"}
              element={<InstitutionRankingList />}
            />
            <Route
              path={"institution-rankings/new"}
              element={<InstitutionRankingForm />}
            />
            <Route
              path={"institution-rankings/:uuid"}
              element={<InstitutionRankingForm />}
            />

            <Route path={"checklists"} element={<ChecklistList />} />
            <Route path={"checklists/new"} element={<ChecklistForm />} />
            <Route path={"checklists/:uuid"} element={<ChecklistForm />} />

            <Route path={"representatives"} element={<RepresentativeList />} />
            <Route
              path={"representatives/new"}
              element={<RepresentativeForm />}
            />
            <Route
              path={"representatives/:uuid"}
              element={<RepresentativeForm />}
            />

            <Route path={"interactions"} element={<InteractionList />} />
            <Route path={"interactions/new"} element={<InteractionForm />} />
            <Route path={"interactions/:uuid"} element={<InteractionForm />} />

            <Route path="users" element={<UserList />} />
            <Route path="users/profile" element={<UserProfile />} />
            <Route path="users/:uuid" element={<UserDetailComponent />} />
            <Route
              path="users/:uuid/journey-tracker"
              element={<UserPlacementEnvironmentComponent />}
            />

            <Route path={"admin"} element={<Admin />} />
            <Route path={"admin/contexts"} element={<ContextList />} />
            <Route path={"admin/contexts/new"} element={<ContextForm />} />

            <Route path={"admin/fields"} element={<FieldList />} />
            <Route path={"admin/fields/new"} element={<FieldForm />} />
            <Route path={"admin/fields/:uuid"} element={<FieldForm />} />

            <Route path={"admin/tags"} element={<TagList />} />
            <Route path={"admin/tags/new"} element={<TagForm />} />

            <Route
              path={"admin/campus-activities"}
              element={<CampusActivityList />}
            />
            <Route
              path={"admin/campus-activities/new"}
              element={<CampusActivityForm />}
            />
            <Route
              path={"admin/campus-activities/:uuid"}
              element={<CampusActivityForm />}
            />

            <Route
              path={"people-connection"}
              element={<PeopleConnectionList />}
            />
            <Route
              path={"story-interactions"}
              element={<StoryInteractionsList />}
            />
            <Route path={"voice-requests"} element={<VoiceRequestsList />} />

            <Route
              path={"crm/field-types"}
              element={isAdmin() ? <FieldTypesList /> : <AccessDenied />}
            />
            <Route
              path={"crm/field-types/new"}
              element={isAdmin() ? <FieldTypeForm /> : <AccessDenied />}
            />
            <Route
              path={"crm/field-types/:id"}
              element={isAdmin() ? <FieldTypeEdit /> : <AccessDenied />}
            />
            <Route path={"tally/forms/add"} element={<TallyFormQuestions />} />
            <Route path={"tally/forms/:id"} element={<TallyFormDetail />} />
            <Route path={"tally/forms"} element={<TallyFormList />} />

            <Route path="/people" element={<PeopleList />} />
            <Route path="/people/new" element={<PeopleForm />} />
            {isAccountManager() ? (
              <Route path="/people/:uuid" element={<PeopleShow />} />
            ) : (
              <Route path="/people/:uuid" element={<PeopleForm />} />
            )}
            <Route path="/accounts" element={<AccountList />} />
            <Route path="/accounts/new" element={<AccountForm />} />
            <Route path="/accounts/:uuid" element={<AccountForm />} />

            <Route path={""} element={<Homepage />} />

            {isAdmin() && (
              <Route path="/dashboard" element={<MetabaseExamplePage />} />
            )}
          </Route>
        </Routes>
      </appToastContext.Provider>
    </userContext.Provider>
  );
}
